'use client';

import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import Link from 'next/link';
import classNames from 'classnames';

import { TMenuLink } from './types';
import { getSupportedLocales, LanguageCode, LanguageMap } from '@/utils/locales';

import Icon from '@/components/Icon/Icon';
import { getLinkToTranslation } from '@/components/Header/getLinkToTranslation';

const LanguageMenuItem = () => {
    const [isOpen, setIsOpen] = useState(false);
    const [curPosition, setCurPosition] = useState<DOMRect>();
    const menuContainerRef = useRef<HTMLDivElement>(null);
    const [subItems, setSubItems] = useState<TMenuLink[]>([]);

    // Fetch supported locales and set sub-items
    useEffect(() => {
        async function fetchData() {
            const locales = getSupportedLocales();
            // fetch data
            setSubItems(
                locales.map((locale) => ({
                    title: LanguageMap[locale as LanguageCode].name,
                    href: LanguageMap[locale as LanguageCode].href,
                })),
            );
        }
        fetchData();
    }, []);
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (
                isOpen &&
                menuContainerRef.current &&
                !menuContainerRef.current.contains(event.target as Node)
            ) {
                setIsOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [setIsOpen, isOpen, menuContainerRef]);

    return (
        <div ref={menuContainerRef} className="tw-mr-4 tw-relative">
            <Icon
                type="globe"
                className="globe-icon tw-cursor-pointer tw-inline tw-relative tw-z-20"
                onClick={() => setIsOpen((prevState) => !prevState)}
            />
            <MenuSubItem mainItemCurPosition={curPosition} items={subItems} isOpen={isOpen} />
        </div>
    );
};

const MenuSubItem = ({
    items,
    isOpen,
    mainItemCurPosition,
}: {
    isOpen: boolean;
    items: TMenuLink[];
    mainItemCurPosition?: DOMRect;
}) => {
    const subMenuRef = useRef<HTMLDivElement>(null);
    const menuPositionRef = useRef<HTMLSpanElement>(null);
    const [popupRect, setPopupRect] = useState<DOMRect>();
    const [menuOffset, setMenuOffset] = useState(0);
    const [isFinishedAnimating, setIsFinishedAnimating] = useState<boolean | undefined>(true);

    const prevIsOpenRef = useRef<boolean>();

    // Handle animation state changes
    useEffect(() => {
        if (prevIsOpenRef.current && prevIsOpenRef.current === true && !isOpen) {
            setTimeout(() => {
                setIsFinishedAnimating(true);
            }, 200);
        } else if (
            typeof prevIsOpenRef.current !== 'undefined' &&
            prevIsOpenRef.current !== isOpen
        ) {
            setIsFinishedAnimating(false);
        }
        prevIsOpenRef.current = isOpen;
    }, [isOpen]);

    // Set popup and menu offset positions
    useLayoutEffect(() => {
        if (subMenuRef.current) {
            setPopupRect(subMenuRef.current?.getBoundingClientRect());
        }
        if (menuPositionRef.current) {
            setMenuOffset(menuPositionRef.current?.getBoundingClientRect().left ?? 0);
        }
    }, [isOpen]);

    return (
        <>
            <span
                className="tw-absolute tw-w-0 tw-h-0 tw-left-0 tw-top-0"
                ref={menuPositionRef}></span>
            <span
                className={classNames(
                    "tw-transition tw-ease-in-out tw-duration-200 tw-absolute tw-z-10 before:tw-z-10 before:tw-mt-1 before:tw-border-t-[42px] before:tw-drop-shadow-menuArrow before:-tw-translate-y-1/2 before:tw-border-[15px] before:tw-border-solid before:tw-border-b-white before:tw-border-l-transparent before:tw-border-r-transparent before:tw-border-t-transparent before:tw-contents-[''] -tw-translate-x-[25px] before:tw-w-[20px] before:tw-h-[20px] before:!tw-block tw-w-5 tw-h-5 tw-top-2",
                    {
                        'tw-opacity-100 tw-inline': isOpen,
                        'tw-opacity-0 tw-hidden': !isOpen,
                        'tw-top-[-1000px]': isFinishedAnimating,
                    },
                )}
                style={{
                    marginLeft: (mainItemCurPosition?.width ?? 0) / 4,
                }}
            />
            <div
                className={classNames(
                    'tw-transition tw-ease-in-out tw-duration-200 tw-absolute tw-w-[260px] tw-bg-white tw-px-6 tw-pt-2 tw-pb-4 tw-shadow-menu tw-rounded-md tw-top-[40px] tw-right-[-38px]',
                    {
                        'tw-opacity-100 tw-block': isOpen,
                        'tw-opacity-0 tw-hidden': !isOpen,
                        'tw-top-[-1000px]':
                            typeof isFinishedAnimating === 'undefined' || isFinishedAnimating,
                    },
                )}
                ref={subMenuRef}>
                <div>
                    <SubItems items={items} />
                </div>
            </div>
        </>
    );
};

const SubItems = ({ items }: { items: TMenuLink[] }) => {
    if (!items) {
        return null;
    }
    return items.map((item, index) => {
        return (
            <div
                key={index}
                className="tw-my-3"
                onClick={() =>
                    window.dataLayer &&
                    window.dataLayer.push({ event: 'select_language', select_language: item.title })
                }>
                <Link
                    prefetch={false}
                    href={getLinkToTranslation(item.href)}
                    className="tw-flex tw-justify-between tw-align-items-center">
                    <div className="tw-pt-[2.5px]">{item.title}</div>
                </Link>
            </div>
        );
    });
};

export default LanguageMenuItem;
